import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import create from 'zustand';
import { IcdCptForm } from '../../components/coding-tool/icd-cpt';
import { LinkButton } from '../../components/common/presentation';
import { Api } from '../api';
import { auditPerson } from '../audit';
import { auth } from '../auth';
import { ChangeReasons, changeReasonFormApi } from '../change-form';
import { ReviseCodeForm } from '../codesets-search';
import { Auto, Text } from '../controls';
import { Audit, ChangeReason, CodeSet, Diagnosis, EditAction, Procedure, Worksheet, auditRecord } from '../domain';
import { FormApi, formApi } from '../form';
import { IcdCptApi } from '../icd-cpt-api';
import { originalOrange } from '../theme';
import { VisitCode, WorksheetApis } from '../visit-context';
import { currentWorkflow, isReviewWorkflowState } from '../workflow-api';
import { WorksheetElement, elementCode } from '../worksheets';
import { RebuttalAction } from './audit-rebuttal-model';
import { on } from 'events';

type AuditData = {
  auditOpen: boolean;
  title: any;
  content: any;
  audit: Audit;
  onSubmit: () => Promise<boolean>;
  onCancel: () => void;
};

type AuditActions = {
  openAuditWindow(open: boolean, props?: Partial<AuditData>): void;
};

export type AuditRebuttalApi = Api<any, AuditData, AuditActions>;
function auditRebuttalApi(): AuditRebuttalApi {
  const data = create<AuditData>(() => ({
    auditOpen: false,
    title: null,
    content: null,
    audit: null,
    onSubmit: null,
    onCancel: null,
  }));

  const actions = {
    openAuditWindow: (auditOpen: boolean, props?: AuditData) => {
      data.setState({ ...props, ...{ auditOpen } });
    },
  };
  return { configuration: {}, data, actions };
}

export function AuditRebuttalDialogue() {
  const { title, content, onCancel } = auditRebuttal.data.getState();
  const open = auditRebuttal.data((s) => s.auditOpen);

  const onClose = () => {
    auditRebuttal.actions.openAuditWindow(false);
    if (onCancel) {
      onCancel();
    }
  };

  const onSubmit = async () => {
    const fn = auditRebuttal.data.getState().onSubmit;
    if (fn) {
      const complete = await fn();
      if (complete) {
        auditRebuttal.actions.openAuditWindow(false);
      }
    }
  };

  return (
    <Dialog open={open} maxWidth={false}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        style={{
          paddingTop: 0,
          zIndex: 1501,
          marginLeft: 12,
        }}
      >
        {content}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const auditRebuttal = auditRebuttalApi();

const width = { width: '100%' };
type IcdCptProps = {
  api: IcdCptApi;
  title?: string;
};
export function openCptAudit({ api, title }: IcdCptProps) {
  const commentForm = formApi({
    values: { comments: null },
    fields: {
      comments: {
        label: 'Comments',
        style: { width: '100%' },
        multiline: true,
        rows: 2,
      },
    },
  });

  auditRebuttal.actions.openAuditWindow(true, {
    title,
    content: (
      <Grid container direction="column" style={{ ...width, ...{ padding: 0 } }}>
        <IcdCptForm icdCpt={api} />

        <Grid item style={{ ...width, ...{ padding: 0 } }}>
          <Text {...commentForm.controls.comments} />
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

type AuditRebuttalFormData = {
  reason: VisitCode;
  notes: null;
};

function auditReasonForm(list: VisitCode[], onSubmit: (form: AuditRebuttalFormData) => void, existing?: Audit): FormApi<AuditRebuttalFormData> {
  const i = existing?.changeReasons?.length;
  const changeReason: ChangeReason = i > 0 ? existing.changeReasons[i - 1] : null;
  const reason = changeReason ? list.filter((c) => c.code === changeReason.changeReasonCode)[0] : null;

  return formApi<AuditRebuttalFormData>({
    values: {
      reason,
      //@ts-ignore cast
      notes: '',
    },
    fields: {
      reason: {
        required: true,
        label: 'Change Reason',
        list,
        listLabel: (v: VisitCode) => v.description,
      },
      notes: {
        label: 'Audit Comments',
        style: { width: '100%' },
        multiline: true,
        rows: 2,
      },
    },
    valid: (v) => !!v.reason,
    onSubmit: (v) => {
      onSubmit(v);
    },
  });
}

type AuditActionProps = {
  title: string;
  list: VisitCode[];
  onComplete: (a: Audit) => void;
  target?: CodeSet;
  existing?: Audit;
  revise?: boolean;
  onCancel?: () => void;
  worksheet?: Worksheet;
};

export function openAuditRebuttalAction(props: AuditActionProps) {
  const { title, list, onComplete, revise, target } = props;
  let existing = props.existing;

  const form: FormApi = auditReasonForm(
    list,
    (values) => {
      const update: Audit = { ...existing };
      const { code, description } = values.reason;

      update.changeReasonCode = code as string;
      update.changeReasonDescription = description;
      update.notes = values.notes;

      update.changeReasons = update.changeReasons || [];
      const reason: ChangeReason = {
        fieldName: 'object',
        code: elementCode(target),
        oldValue: '',
        newValue: '',
        changeReasonCode: update.changeReasonCode,
        changeReasonDescription: update.changeReasonDescription,
      };

      if (!revise) {
        update.changeReasons.push(reason);
      }

      // if (!update.createdBy || !update.createdBy?.firstName) {
      //   update.createdBy = auth.signedIn;
      // }

      form.valid.setState({ value: true });
      onComplete(update);
    },
    existing
  );
  const { notes, reason } = form.controls;

  //TODO other worksheet elements...
  const onReviseComplete = (code: Diagnosis | Procedure | any) => {
    code = code || target;
    if (code) {
      code.audit = {
        ...{ createdBy: auditPerson(auth.signedIn) },
        ...existing,
        ...code?.audit,
        ...{
          action: 'UPDATE',
          code: code.diagnosisCode || code.cptCode,
        },
      };

      code.code = code.diagnosisCode || code.cptCode;

      if (code.diagnosisCode) {
        code.diagnosisCode = code.audit.code;
      } else {
        code.cptCode = code.audit.code;
      }
      form.submit();

      existing = { ...existing, ...code.audit };

      onComplete(code.audit);
    }
  };

  auditRebuttal.actions.openAuditWindow(true, {
    title,
    onSubmit: async () => {
      await form.submit();
      return form.valid.getState().value;
    },
    content: (
      <Grid
        container
        direction="column"
        spacing={2}
        style={{
          ...width,
          ...{ paddingTop: 0, width: 346 },
        }}
      >
        {list?.length > 0 && (
          <Grid item style={{ ...width }}>
            <Auto {...reason} />
          </Grid>
        )}
        {revise && (
          <Grid item style={width}>
            <ReviseCodeForm target={target} onCodeFound={onReviseComplete} />
          </Grid>
        )}

        <Grid item style={width}>
          <Text {...notes} />
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

type AuditChangeActionProps = {
  title: string;
  original: any;
  update: any;
  changeReasons: ChangeReasons;
  fieldNames: string[];
  onComplete: (a: Audit) => void;
  onNoChanges?: () => void;
  existing?: Audit;
  onCancel?: () => void;
};

export function openAuditRebuttalChangeAction(props: AuditChangeActionProps) {
  const { title, original, update, changeReasons, onComplete, onNoChanges, fieldNames, onCancel } = props;
  let existing = props.existing || original.audit;

  const api = changeReasonFormApi({
    original,
    update,
    changeReasons,
    fieldNames,
  });

  if (!api?.reasons.length) {
    if (onNoChanges) {
      onNoChanges();
    }
    return;
  }

  const { form, component } = api;

  auditRebuttal.actions.openAuditWindow(true, {
    title,
    onCancel,
    onSubmit: () => {
      return new Promise(async (resolve) => {
        await form.submit();

        const changeReasons = existing?.changeReasons || [];

        const a: Audit = { ...existing };
        a.changeReasons = changeReasons.concat(api.userChangeReasons());
        a.notes = form.values.getState().value.notes || a.notes;

        const valid = form.valid.getState().value;
        if (valid && onComplete) {
          setTimeout(() => onComplete(a), 0);
          return resolve(valid);
        }
        return resolve(valid);
      });
    },
    content: (
      <Grid
        container
        direction="column"
        spacing={2}
        style={{
          ...width,
          ...{ paddingTop: 0, width: 900 },
        }}
      >
        <Grid item style={width}>
          {component}
        </Grid>

        <Grid item style={{ ...width, ...{ flexGrow: 1, paddingBottom: 8 } }}></Grid>
      </Grid>
    ),
  });
}

export function reviewRebuttalActionOr(props: AuditActionProps, fn: () => void, reviewGuard?: () => boolean): void {
  const guard = reviewGuard || isReviewWorkflowState;
  if (guard()) {
    openAuditRebuttalAction(props);
  } else {
    fn();
  }
}

export function reviewRebuttalChangesOr(props: AuditChangeActionProps, fn: () => void): void {
  if (isReviewWorkflowState()) {
    props.onNoChanges = fn;
    openAuditRebuttalChangeAction(props);
  } else {
    fn();
  }
}

export function editRebuttalAction(props: AuditActionProps): void {
  openAuditRebuttalAction(props);
}

export const onEditRebuttalAction =
  (title: string, onComplete: (item?: any) => void, revise = false, list = []) =>
  (e: any) => {
    const audit: Audit = e.value.audit;

    editRebuttalAction({
      title,
      revise,
      list: list || [],
      existing: audit,
      target: e.value,
      onComplete: (a: Audit) => {
        if (revise) {
          a.action = 'UPDATE';
          audit.changeReasonCode = a.changeReasonCode;
          audit.changeReasonDescription = a.changeReasonDescription;
          audit.notes = a.notes;

          const i = audit.changeReasons.length;
          if (i) {
            const b = audit.changeReasons[i - 1];
            if (b) {
              b.changeReasonCode = a.changeReasonCode;
              b.changeReasonDescription = a.changeReasonDescription;
            }
          }
        }
        e.value.audit = { ...e.value.audit, ...a };

        if (onComplete) {
          onComplete(e.value);
        }
      },
    });
  };

export type AuditGroup = {
  workflow: string;
  added: WorksheetElement[];
  deleted: WorksheetElement[];
  moved: WorksheetElement[];
  revised: WorksheetElement[];
  show: boolean;
};
export type AuditSummary = {
  worksheet: Worksheet;
  icd: AuditGroup;
  cpt: AuditGroup;
  downCodes: AuditGroup;
  lostRevenue: AuditGroup;
  tracking: AuditGroup;
  sendBacks: AuditGroup;
  reasonForVisit: AuditGroup;
  ncnbReasons: AuditGroup;
  workflow: string;
  show?: boolean;
};

export function getElementAudit(e: WorksheetElement, workflow: string): Partial<Audit> {
  if (!e) {
    return {};
  }
  const map = e.auditMap || {};
  const withWorkflow = Object.entries(map).filter(([k, _v]) => k === workflow);

  if (!withWorkflow.length) {
    return {};
  }

  if (withWorkflow.length > 1) {
    console.info('Multiple change reasons for single element', withWorkflow);
  }

  const obj = withWorkflow[0][1];
  return obj;
}

export function getAllElementAudits(e: WorksheetElement): Partial<Audit>[] {
  if (!e) {
    return [];
  }
  const map = e.auditMap || {};
  const list: Audit[] = [];

  Object.entries(map).forEach(([workflow, v]) => {
    if (v.changeReasons?.length) {
      list.push({ ...v, workflow });
    }
  });
  return list;
}

function auditRebuttalSummary(api: WorksheetApis, workflow: string): AuditSummary {
  const { worksheet } = api.context;
  console.info('auditSummary for workflow', workflow);

  const filter = (t: EditAction) => (v: WorksheetElement) => {
    const audit = getElementAudit(v, workflow);
    if (audit) {
      try {
        const map = v.auditMap || {};
        const existing = map.hasOwnProperty[workflow] ? map[workflow] : {};
        map[workflow] = JSON.parse(JSON.stringify({ ...existing, ...audit }));
        v.auditMap = map;
      } catch (e) {
        console.info('audit parse error', e);
      }
    }
    v.audit = audit;
    return audit?.action === t;
  };

  const add = filter('CREATE');
  const remove = filter('DELETE');
  const revise = filter('UPDATE');

  function group(list: WorksheetElement[] = [], moved = []): AuditGroup {
    const g: AuditGroup = {
      workflow,
      moved,
      added: list.filter(add),
      deleted: list.filter(remove),
      revised: list.filter(revise),
      show: false,
    };
    g.show = !!(g.added?.length || g.deleted?.length || g.moved?.length || g.revised?.length);
    return g;
  }

  const { diagnoses, procedures, reasonForVisit: rfv } = api.icdCpt.appendInactive(api.icdCpt.current());

  const icd: AuditGroup = group(diagnoses, worksheet.diagnosisMoves || []);
  const cpt: AuditGroup = group(procedures, worksheet.procedureMoves || []);

  const downCodes: AuditGroup = group(api.downCode.list.data.getState().all);
  const lostRevenue = group(api.lostRevenue.list.data.getState().all);
  const tracking = group(api.trackingCode.list.data.getState().all);
  const sendBacks = group(api.returns.list.data.getState().all);
  const reasonForVisit = group(rfv);
  const ncnbReasons = group(api.ncnbReasons.list.data.getState().all);

  return {
    worksheet,
    workflow,
    icd,
    cpt,
    downCodes,
    lostRevenue,
    tracking,
    sendBacks,
    reasonForVisit,
    ncnbReasons,
    show: !!(icd.show || cpt.show || downCodes.show || lostRevenue.show || tracking.show || reasonForVisit.show || sendBacks.show || ncnbReasons.show),
  };
}

export function auditRebuttalSummaries(apis: WorksheetApis[], workflows: string[]): AuditSummary[] {
  return (apis || []).map((v) => workflows.map((workflow) => auditRebuttalSummary(v, workflow))).flat();
}

export function updateRebuttalElementAudit(el: WorksheetElement, update: Audit, add = false): void {
  const a: Audit = { ...{ changeReasons: [] }, ...el?.audit, ...update };

  if (a.changeReasons.length && !add) {
    const r = a.changeReasons[a.changeReasons.length - 1];
    a.changeReasonCode = a.changeReasonCode || r.changeReasonCode;
    a.changeReasonDescription = a.changeReasonDescription || r.changeReasonDescription;
  } else {
    a.changeReasons = a.changeReasons || [];
    if (update.changeReasons.length) {
      a.changeReasons.push(update.changeReasons[0]);
    }
  }

  a.action = a.action || 'UPDATE';
  //a.createdBy = auth.signedIn || a.createdBy;

  if (el) {
    a.code = elementCode(el) || a.code;
    el.audit = a;

    el.auditMap = el.auditMap || {};
    const current = currentWorkflow();
    el.auditMap[current] = a;
    el.audit = el.auditMap[current];
  }

  // console.info('updateElementAudit', update, el);
}

type RebuttalActionProps = {
  audit: Audit;
  action: RebuttalAction;
  worksheet?: Worksheet;
  reasons?: VisitCode[];
  workflow?: string;
  onSubmit?: (a: Audit) => void;
};

export function RebuttalActionLink({ audit, action, worksheet, reasons, onSubmit, ...rest }: RebuttalActionProps) {
  const label = action || '';

  const workflow = rest.workflow || audit.workflow;

  function onClick() {
    openAuditRebuttalAction({
      title: `${label} Audit Action`,
      existing: audit,
      list: (reasons || audit?.changeReasons || []) as any,
      onComplete: (update: Audit) => {
        if (workflow) {
          console.info('RebuttalActionLink', update);
          worksheet.auditInfo = auditRecord(workflow, worksheet.auditInfo);

          switch (action) {
            case 'Disagree': {
              worksheet.auditInfo[workflow].hasDisagreement = true;
              break;
            }
            case 'Agree': {
              worksheet.auditInfo[workflow].hasDisagreement = false;
              break;
            }
          }
        } else {
          console.info('RebuttalActionLink - no workflow', update);
        }

        update.rebuttalReasons ||= [];
        const reason: ChangeReason = { ...(update.changeReasons[update.changeReasons.length - 1] || {}) };
        reason.comments = update.notes;
        reason.changeReasonDescription = update.changeReasonDescription;
        update.rebuttalReasons.push(reason);

        if (onSubmit) {
          onSubmit(update);
        }
      },
    });
  }
  return (
    <>
      <LinkButton label={label} onClick={onClick} color={originalOrange} style={{ paddingLeft: 12 }} />
    </>
  );
}
